import React from 'react';
import classnames from 'classnames';

import { PromotionPreview, PromotionsCarousel } from 'components/promotions';
import { Breadcrumbs } from 'components/ui';
import { APP_ROUTES } from 'constants/routes';
import { parseBanner } from 'helpers/ProductsListingHelpers';
import { useMediaQuery } from 'hooks/useMediaQuery';
import { ProductsListing } from 'templates/common';

import type {
  IListingBanner, IPageUrlParams, IPromotionItem, IPromotionListing
} from 'types';

import styles from './PromotionTemplate.module.scss';

const BREADCRUMBS = [
  {
    href: APP_ROUTES.HOME,
    name: 'NAVIGATION.HOME',
    translate: true
  },
  {
    href: APP_ROUTES.PROMOTIONS,
    name: 'NAVIGATION.PROMOTIONS',
    translate: true
  }
];

interface Props {
  urlParams: IPageUrlParams,
  promotion: IPromotionListing,
  promotions: IPromotionItem[]
}

const PromotionTemplate = (props: Props) => {
  const {
    urlParams,
    promotion,
    promotions,
  } = props;

  // Hooks

  const isBreakpoint = useMediaQuery(`(max-width: ${styles.breakpoint})`);

  const [selectedPromotionCode, setSelectedPromotionCode] = React.useState(promotion?.code);
  const [upcomingPromotionIndex, setUpcomingPromotionIndex] = React.useState(getUpcomingPromotionIndex(promotions, promotion?.code));

  // Props

  const {
    analyticsListId,
    analyticsListName,
    code,
    name,
    items: products,
    total: count,
    pages: totalPages,
    page: activePage,
    categories,
    filters,
    orderBy,
    datesLong,
    banners = [],
  } = promotion || {};

  // Effects

  React.useEffect(() => {
    setSelectedPromotionCode(code);
  }, [code]);

  React.useEffect(() => {
    setUpcomingPromotionIndex(getUpcomingPromotionIndex(promotions, selectedPromotionCode));
  }, [promotions, selectedPromotionCode]);

  // Banners

  const parsedBanners: IListingBanner[] = React.useMemo(() => {
    return banners?.map(parseBanner).filter((banner): banner is IListingBanner => !!banner);
  }, [banners]);

  // Render

  return (
    <div className={styles.root}>

      {/* Breadcrumbs */}
      <Breadcrumbs
        className={styles.breadcrumbs}
        items={BREADCRUMBS}
      />

      {/* Promotions Carousel */}
      <PromotionsCarousel
        //
        className={classnames(styles.carousel, styles.promotionsCarousel)}
        sliderClassName={styles.slider}
        slideClassName={styles.slide}
        //
        promotions={promotions}
        selectedCode={selectedPromotionCode}
        //
        cardProps={{
          withTitle: true,
          className: styles.card,
          detailsClassName: styles.details,
        }}
        //
        data-test-id="promotions-carousel"
      />

      {/* Title / Subtitle */}
      <div className={styles.subheader}>
        {name && <div className={styles.subheaderTitle}>{name}</div>}
        {/* eslint-disable-next-line react/no-danger */}
        {datesLong && <div className={styles.subheaderSubtitle} dangerouslySetInnerHTML={{ __html: datesLong }} />}
      </div>

      {/* Products Listing */}
      <ProductsListing
        wide
        className={styles.listing}
        emptyClassName={styles.empty}
        //
        categoryCode={code}
        title={name}
        urlParams={urlParams}
        //
        products={products}
        productListProps={{
          analyticsListId,
          analyticsListName
        }}
        promotion={promotion}
        //
        filters={filters}
        orderBy={orderBy}
        //
        navigation={{
          getSubcategoryLink
        }}
        categories={categories}
        count={count}
        totalPages={totalPages}
        activePage={activePage}
        //
        banners={parsedBanners}
        //
        scrollToProducts={isBreakpoint}
        dataTestId="promo-products-list"
      />

      {/* Upcoming Promotion Preview */}
      {
        upcomingPromotionIndex && promotions[upcomingPromotionIndex]
          ? (
            <div className={styles.preview}>
              <PromotionPreview
                index={upcomingPromotionIndex}
                promotion={promotions[upcomingPromotionIndex]}
              />
            </div>
          ) : null
      }
    </div>

  );
};

// Helpers

const getSubcategoryLink = (
  categoryCode: string,
  subcategoryCode: string
) => {
  return `${APP_ROUTES.PROMOTIONS}/${categoryCode}/${subcategoryCode}`;
};

const getUpcomingPromotionIndex = (
  promotions: IPromotionItem[],
  currentPromotionCode: string
) => {
  if (!promotions.length) return null;

  const currentIndex = promotions.findIndex(({ code }) => code === currentPromotionCode);

  return promotions.length === currentIndex + 1
    ? null
    : currentIndex + 1;
};

// Export

export default PromotionTemplate;
